/**
 * "Animate this image" (Dax, 2026-10-05): one metered Veo generation from a still. The confirm says what it
 * makes and how many of the organisation's monthly allowance are left; the clip appears at once in the still's
 * shoot, working, and lands there analysed like any uploaded clip.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { ANIMATE_SECONDS } from '@studio/contracts';
import { queryClient, trpc } from '@/lib/trpc';

export interface AnimateTarget {
  id: string;
  name: string;
}

export function AnimateConfirm({ still, onClose }: { still: AnimateTarget; onClose: () => void }) {
  const allowance = useQuery(trpc.assets.animateAllowance.queryOptions());
  const animate = useMutation(trpc.assets.animate.mutationOptions({
    onSuccess: async () => {
      onClose();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.library.list.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.search.browse.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.assets.animateAllowance.queryKey() }),
      ]);
    },
  }));
  const left = allowance.data ? Math.max(0, allowance.data.allowance - allowance.data.used) : null;
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal managed-modal" role="dialog" aria-label="Animate this image">
        <h2>Animate this image</h2>
        <span className="sub">&quot;{still.name}&quot; becomes a {ANIMATE_SECONDS}-second clip with gentle, natural movement, filed with its shoot.</span>
        {allowance.data && <span className="sub">{left} of {allowance.data.allowance} left this month</span>}
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={animate.isPending || left === 0 || !allowance.data}
            onClick={() => animate.mutate({ assetId: still.id })}>
            {animate.isPending ? 'Starting…' : 'Animate'}
          </button>
        </div>
        {animate.isError && <div className="error" role="alert">{animate.error.message}</div>}
      </div>
    </>
  );
}

/** A still's latest animation in a few words, for a chip; null when it was never animated or it finished. */
export function animationLabel(animation: { status: string; error: string | null } | null | undefined): { text: string; tone: 'acc' | 'warn'; title?: string | undefined } | null {
  if (!animation) return null;
  if (animation.status === 'queued' || animation.status === 'running') return { text: 'Animating…', tone: 'acc' };
  if (animation.status === 'refused') return { text: 'Animation refused', tone: 'warn', title: animation.error ?? undefined };
  if (animation.status === 'failed') return { text: 'Animation failed', tone: 'warn', title: animation.error ?? undefined };
  return null;
}
